import { X } from "lucide-react";
import { Button, Text } from "@musa.tech/design-system";

export interface FilterChip<K extends string = string> {
  key: K;
  /**
   * Ausente no período (filtro de valor único) e no chip-resumo de um filtro
   * de lista com muitos valores selecionados ("N selecionados") — nos dois
   * casos, remover o chip limpa o filtro inteiro em vez de um valor pontual.
   */
  value?: string;
  /** Nome do filtro — linha de baixo do chip. */
  label: string;
  /** Valor legível — linha de cima do chip. */
  display: string;
}

interface AppliedFilterChipsProps<K extends string = string> {
  chips: FilterChip<K>[];
  onRemove: (key: K, value?: string) => void;
  onClearAll: () => void;
}

/**
 * Contador, chips removíveis e "Limpar todos os filtros".
 * Visual do Figma 124:12976. Não renderiza nada sem filtro aplicado.
 *
 * Genérico em `K` para servir qualquer barra de filtro do app (Dashboard,
 * Financeiro) — cada uma define suas próprias chaves de filtro.
 */
export const AppliedFilterChips = <K extends string = string>({
  chips,
  onRemove,
  onClearAll,
}: AppliedFilterChipsProps<K>) => {
  if (chips.length === 0) return null;

  return (
    <div className="flex w-full flex-col items-stretch gap-2 md:flex-row md:items-center md:justify-between">
      <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:flex-wrap md:items-center md:gap-4">
        <Text variant="label" size="small" className="shrink-0 text-neutral-600">
          {chips.length}{" "}
          {chips.length === 1 ? "filtro aplicado" : "filtros aplicados"}
        </Text>

        {chips.map((chip) => (
          <div
            key={`${chip.key}-${chip.value ?? "unico"}`}
            className="flex h-[46px] w-full items-center justify-between gap-2 rounded-lg border border-neutral-200 bg-neutral-100 py-1 pl-3 pr-1 md:w-auto md:justify-start"
          >
            <div className="flex min-w-0 flex-col items-start">
              <Text
                variant="label"
                size="xsmall"
                className="truncate text-neutral-800"
              >
                {chip.display}
              </Text>
              <Text
                variant="body"
                size="xsmall"
                className="truncate text-neutral-600"
              >
                {chip.label}
              </Text>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onRemove(chip.key, chip.value)}
              aria-label={`Remover filtro ${chip.label}: ${chip.display}`}
              className="size-5 shrink-0 rounded-sm text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800"
            >
              <X aria-hidden="true" />
            </Button>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="tertiary"
        onClick={onClearAll}
        className="h-[25px] w-full shrink-0 rounded px-2.5 font-inter text-sm font-medium leading-5 text-neutral-600 md:w-auto"
      >
        Limpar todos os filtros
      </Button>
    </div>
  );
};
